import { Component, Input } from '@angular/core';

import { FeatureFlagService } from 'src/app/core/services/feature-flag.service';
import { getErpSyncStatusStyles } from 'src/app/shared/utils/quotation-status-styles';
import { IQuotationDetail } from '../../models/quotations.models';

/**
 * WB-8420 — the ERP side of an order's identity, inside the Order references
 * block of both detail panels (the order detail page and the side-by-side).
 *
 * Deliberately narrow: this answers "did it get there, and under what number",
 * so it renders only while the answer is in progress or yes. A settled failure
 * is not shown here -- it already owns the full-width banner above the order,
 * and repeating it in a reference list would bury it. `none`, `canceled` and
 * `not_applicable` have nothing to reference at all.
 *
 * The rows mirror `<ui-detail-field>`'s geometry rather than reusing it: the
 * value is a status tag and a copy affordance, not the text/lookup/date shapes
 * that component exists to edit.
 */
@Component({
  selector: 'app-erp-sync-details',
  templateUrl: './erp-sync-details.component.html',
  styleUrls: ['./erp-sync-details.component.scss'],
})
export class ErpSyncDetailsComponent {
  constructor(public featureFlags: FeatureFlagService) {}

  @Input() quotation: IQuotationDetail | undefined;

  get syncStatus(): string {
    return this.quotation?.erp_sync?.sync_status || 'none';
  }

  get inProgress(): boolean {
    return ['pending', 'processing'].includes(this.syncStatus);
  }

  get isSynced(): boolean {
    return this.syncStatus === 'success';
  }

  get show(): boolean {
    return this.featureFlags.asyncErpSyncEnabled && (this.inProgress || this.isSynced);
  }

  /** The number the ERP assigned. Absent until the sync lands, by contract. */
  get erpId(): string | null {
    return this.quotation?.erp_sync?.erp_id || null;
  }

  getStatusStyles(): { [key: string]: string } {
    return getErpSyncStatusStyles(this.syncStatus);
  }
}
